import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import PointConversionsApi from 'api/PointConversionsApi';
import Button from 'components/atoms/ui/Button';
import FlexibleModalContainer from 'containers/FlexibleModalContainer';
import { POINT_CONVERSION_VALIDATE_OPERATION } from 'constants/api';
import { BUTTON_MAIN_TYPE } from 'constants/ui';
import { IPointsConverion } from 'interfaces/components/wall/Ipoints';
import modalStyle from 'assets/style/components/Modals/Modal.module.scss';
import pointsStyle from 'sass-boilerplate/stylesheets/components/launch/Points.module.scss';

export const isReservedConversion = (conversion: IPointsConverion) =>
  Number(conversion?.status) === 2 && Boolean(conversion?.reconciliationStartedAt) && !conversion?.orderUuid;

export const HuurayResolutionModal = ({ conversion, onClose, onValidateSuccess }: {
  conversion: IPointsConverion | null;
  onClose: () => void;
  onValidateSuccess: (conversion: { id: number }) => void;
}) => {
  const { formatMessage } = useIntl();
  const api = useMemo(() => new PointConversionsApi(), []);
  const [checking, setChecking] = useState(false);

  const formatDate = (value?: string | null) => value ? new Date(value).toLocaleString() : '—';
  const dueAt = conversion?.reconciliationDeadlineAt || (conversion?.reconciliationNotifiedAt
    ? new Date(new Date(conversion.reconciliationNotifiedAt).getTime() + 72 * 60 * 60 * 1000).toISOString()
    : null);
  const details = conversion ? [
    [formatMessage({ id: 'wall.pointConversions.name' }), conversion.name],
    [formatMessage({ id: 'wall.pointConversions.email' }), conversion.email],
    [formatMessage({ id: 'wall.pointConversions.platformName' }), conversion.platform?.name],
    [formatMessage({ id: 'wall.pointConversions.programName' }), conversion.program?.name],
    [formatMessage({ id: 'wall.pointConversions.points' }), `${conversion.points} WINS`],
    [formatMessage({ id: 'wall.pointConversions.amount' }), `${conversion.amount} ${conversion.currency || 'EUR'}`],
    [formatMessage({ id: 'wall.pointConversions.transactionRefId' }), conversion.transactionRefId],
    [formatMessage({ id: 'wall.pointConversions.createdAt' }), formatDate(conversion.createdAt)],
    [formatMessage({ id: 'pointsConversions.reconciliation.notifiedAt' }), formatDate(conversion.reconciliationNotifiedAt)],
    [formatMessage({ id: 'pointsConversions.reconciliation.deadline' }), dueAt ? formatDate(dueAt) : formatMessage({ id: 'pointsConversions.reconciliation.notificationPending' })],
    [formatMessage({ id: 'pointsConversions.reconciliation.lastCheckedAt' }), formatDate(conversion.reconciliationLastCheckedAt)]
  ] : [];

  const verify = async () => {
    if (!conversion?.id || checking) return;
    setChecking(true);
    try {
      await api.validatePointConversion({ id: conversion.id, operation: POINT_CONVERSION_VALIDATE_OPERATION });
      onValidateSuccess({ id: Number(conversion.id) });
      toast.success(formatMessage({ id: 'pointsConversions.reconciliation.confirmed' }));
      onClose();
    } catch (_error) {
      toast.error(formatMessage({ id: 'pointsConversions.reconciliation.notConfirmed' }));
    } finally {
      setChecking(false);
    }
  };

  return (
    <FlexibleModalContainer
      isModalOpen={Boolean(conversion)}
      closeModal={() => !checking && onClose()}
      className={modalStyle.largeModal}
      animationClass={pointsStyle.resolutionModalAnimation}
      fullOnMobile={false}
    >
      {conversion && (
        <div className={pointsStyle.resolutionModal}>
          <h2>{formatMessage({ id: 'pointsConversions.reconciliation.modalTitle' })} #{conversion.id}</h2>
          <p>{formatMessage({ id: 'pointsConversions.reconciliation.reservedHelp' })}</p>
          <dl>
            {details.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt><dd>{value || '—'}</dd>
              </div>
            ))}
          </dl>
          <div className={pointsStyle.resolutionModalActions}>
            <Button onClick={verify} disabled={checking} loading={checking}>
              {formatMessage({ id: 'pointsConversions.reconciliation.verify' })}
            </Button>
            <Button type={BUTTON_MAIN_TYPE.DANGER} onClick={onClose} disabled={checking}>
              {formatMessage({ id: 'pointsConversions.reconciliation.close' })}
            </Button>
          </div>
        </div>
      )}
    </FlexibleModalContainer>
  );
};
